import { useAtomValue, useSetAtom } from 'jotai';

import { useAccountUpdate } from '@consumer-frontend/nc-api';
import {
  deepEqual,
  IncomeDataT,
  isEmptyObject,
  modifiedAccountFields,
  registrationTransform,
  somethingWentWrongAlertBannerProps,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import { useApplyRouting } from '../../routing';
import {
  accountAtom,
  alertBannerAtom,
  incomeDataAtom,
  isIncomeDetailsRequiredAtom,
  registrationDataAtom,
} from '../../store';
import { NcIncomeSourceForm } from '../NcIncomeSourceForm';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';
import { NcProgressBar } from '../NcProgressBar';

export const NcVerifyIncomePage: React.FC = () => {
  const account = useAtomValue(accountAtom);
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const { trigger: updateAccount } = useAccountUpdate();
  const incomeData = useAtomValue(incomeDataAtom);
  const registrationData = useAtomValue(registrationDataAtom);
  const {
    handleBack,
    handleContinue,
    progress,
  } = useApplyRouting();
  const setIsIncomeDetailsRequired = useSetAtom(isIncomeDetailsRequiredAtom);

  /**
   * Set default form values from jotai store or account data. This assumes that account data is fetched
   * and parsed by authenticated route middleware. This can be removed if incomeData store value is set
   * by middleware as well.
   */
  const defaultFormValues = account?.income && isEmptyObject(incomeData) ? account.income : incomeData;

  const onSubmit = async (formData: IncomeDataT) => {
    const accountIncomeData = {
      amount: account?.income?.amount,
      benefits: account?.income?.benefits,
      employment: account?.income?.employment,
      source: account?.income?.source,
    };

    /**
     * Compare form data to income data from account API response.
     * We can't rely on the form's "isDirty" state because the income
     * data in the jotai store that populates the form may already be modified
     * from the next form step.
     */
    const isIncomeDataModified = !deepEqual(formData, accountIncomeData);
    const hasIncomeDetails = !!(incomeData?.frequency || incomeData?.method);
    const isIncomeDetailsRequired = isIncomeDataModified || hasIncomeDetails;

    // atom value used for determining next route
    setIsIncomeDetailsRequired(isIncomeDetailsRequired);

    // if values have changed or income details present, continue to the next step
    if (isIncomeDetailsRequired) {
      await handleContinue({}, formData);

      return;
    }

    // only need to update account info if it has changed
    const modifiedFields = account ? modifiedAccountFields(account, registrationData) : undefined;

    if (modifiedFields && !isEmptyObject(modifiedFields)) {
      const serializedRegistrationData = registrationTransform.serialize(modifiedFields);

      const accountUpdated = await updateAccount(serializedRegistrationData);

      if (!accountUpdated) {
        setAlertBanner(somethingWentWrongAlertBannerProps);

        return;
      }
    }

    await handleContinue({}, formData);
  };

  return (
    <NcPageWithAlertBanner>
      <Container className="nc-income-source-page" hasBoxShadow>
        <NcProgressBar progress={progress} />

        <h1 className="h3">Verify Income</h1>

        <NcIncomeSourceForm
          incomeData={defaultFormValues}
          onBack={handleBack}
          onSubmitComplete={onSubmit}
          resetToInitialValues
          showAmountField
        />

      </Container>
    </NcPageWithAlertBanner>
  );
};
